import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { EmbedProvider, useEmbedContext } from '@/hooks/useEmbedContext';
import { useRouteSync } from '@/hooks/useRouteSync';
import { QuestionsPage } from '@/pages/QuestionsPage';
import { QuestionDetailPage } from '@/pages/QuestionDetailPage';
import { StatsPage } from '@/pages/StatsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Syncs child app route with parent shell URL for deep linking */
function RouteSync() {
  useRouteSync();
  return null;
}

function AppRoutes() {
  const { isEmbedded, isReady, embedParams } = useEmbedContext();

  // If not embedded and no token, show message to access via app-shell
  if (!isEmbedded && !embedParams.token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-8">
        <div className="max-w-md text-center">
          <h1 className="mb-4 text-2xl font-bold">Questions</h1>
          <p className="text-muted-foreground">
            This application is designed to be accessed through the main
            portal. Please log in to the portal to access the Questions
            dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Show loading while waiting for embed context
  if (!isReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/questions" element={<QuestionsPage />} />
      <Route path="/questions/stats" element={<StatsPage />} />
      <Route path="/questions/:id" element={<QuestionDetailPage />} />
      <Route path="*" element={<Navigate to="/questions" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <EmbedProvider>
          <RouteSync />
          <div className="min-h-screen bg-background text-foreground">
            <AppRoutes />
          </div>
        </EmbedProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
